import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Spinner from '../../components/Spinner';
import './AdminSettings.css';

function AdminSettings() {
  const [settings, setSettings] = useState({
    sandbox_mode: false,
    jokes_ticker_enabled: true,
    jokes_categories: '',
    jokes_max_length: 150,
    jokes_blacklist_flags: '',
    seasonal_animation_enabled: false,
    seasonal_animation_type: 'snow',
    seasonal_animation_count: 25,
    seasonal_animation_speed: 'medium'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [testingJokes, setTestingJokes] = useState(false);
  const [testJokes, setTestJokes] = useState([]);

  // Available joke categories
  const availableCategories = ['Programming', 'Miscellaneous', 'Dark', 'Pun', 'Spooky', 'Christmas'];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/settings', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      if (response.data.success) {
        const { settings: fetchedSettings } = response.data;
        setSettings({
          sandbox_mode: fetchedSettings.sandbox_mode === 'true' || fetchedSettings.sandbox_mode === true,
          jokes_ticker_enabled: fetchedSettings.jokes_ticker_enabled === 'true' || fetchedSettings.jokes_ticker_enabled === true,
          jokes_categories: fetchedSettings.jokes_categories || '',
          jokes_max_length: parseInt(fetchedSettings.jokes_max_length) || 150,
          jokes_blacklist_flags: fetchedSettings.jokes_blacklist_flags || '',
          seasonal_animation_enabled: fetchedSettings.seasonal_animation_enabled === 'true' || fetchedSettings.seasonal_animation_enabled === true,
          seasonal_animation_type: fetchedSettings.seasonal_animation_type || 'snow',
          seasonal_animation_count: parseInt(fetchedSettings.seasonal_animation_count) || 25,
          seasonal_animation_speed: fetchedSettings.seasonal_animation_speed || 'medium'
        });
      }
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const response = await axios.put('/api/admin/settings', {
        settings: {
          sandbox_mode: settings.sandbox_mode ? 'true' : 'false',
          jokes_ticker_enabled: settings.jokes_ticker_enabled ? 'true' : 'false',
          jokes_categories: settings.jokes_categories,
          jokes_max_length: settings.jokes_max_length.toString(),
          jokes_blacklist_flags: settings.jokes_blacklist_flags,
          seasonal_animation_enabled: settings.seasonal_animation_enabled ? 'true' : 'false',
          seasonal_animation_type: settings.seasonal_animation_type,
          seasonal_animation_count: settings.seasonal_animation_count.toString(),
          seasonal_animation_speed: settings.seasonal_animation_speed
        }
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      if (response.data.success) {
        showMessage('success', 'Settings saved successfully!');

        // If sandbox mode changed, show notification
        if (settings.sandbox_mode) {
          showMessage('warning', 'SANDBOX MODE ENABLED: Using Stripe test keys and skipping Printify orders');
        }
      }
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestJokes = async () => {
    try {
      setTestingJokes(true);
      setTestJokes([]);
      setMessage({ type: '', text: '' });

      // Force refresh jokes cache
      await axios.post('/api/jokes/refresh', {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      // Fetch fresh jokes
      const response = await axios.get('/api/jokes/feed');
      if (response.data.jokes) {
        setTestJokes(response.data.jokes.slice(0, 5)); // Show first 5
        showMessage('success', `Fetched ${response.data.count} jokes successfully!`);
      }
    } catch (error) {
      showMessage('error', 'Failed to fetch test jokes');
    } finally {
      setTestingJokes(false);
    }
  };

  const toggleCategory = (category) => {
    const categories = settings.jokes_categories.split(',').map(c => c.trim()).filter(c => c);
    const index = categories.indexOf(category);

    if (index > -1) {
      categories.splice(index, 1);
    } else {
      categories.push(category);
    }

    setSettings({ ...settings, jokes_categories: categories.join(',') });
  };

  const isCategorySelected = (category) => {
    const categories = settings.jokes_categories.split(',').map(c => c.trim());
    return categories.includes(category);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  if (loading) {
    return (
      <div className="admin-settings">
        <h1>Settings</h1>
        <Spinner text="Loading settings..." />
      </div>
    );
  }

  return (
    <div className="admin-settings">
      <h1>Application Settings</h1>

      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Sandbox Mode Section */}
      <section className="settings-section">
        <h2>🧪 Sandbox Mode</h2>
        <p className="section-description">
          Enable test mode for development and testing. When enabled, Stripe test keys will be used and orders will NOT be sent to Printify.
        </p>

        <div className="setting-row">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={settings.sandbox_mode}
              onChange={(e) => setSettings({ ...settings, sandbox_mode: e.target.checked })}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-text">
              {settings.sandbox_mode ? 'SANDBOX MODE ACTIVE' : 'Live Mode'}
            </span>
          </label>
        </div>

        {settings.sandbox_mode && (
          <>
            <div className="warning-box">
              ⚠️ <strong>Sandbox Mode is ON:</strong>
              <ul>
                <li>Using Stripe TEST API keys</li>
                <li>Orders will NOT be sent to Printify</li>
                <li>Order numbers will be prefixed with "SANDBOX-"</li>
                <li>Use Stripe test cards below</li>
              </ul>
            </div>

            <div className="info-box test-cards-box">
              <strong>💳 Stripe Test Card Numbers:</strong>
              <div className="test-cards-grid">
                <div className="test-card-item">
                  <div className="card-number">4242 4242 4242 4242</div>
                  <div className="card-desc">Visa - Success</div>
                </div>
                <div className="test-card-item">
                  <div className="card-number">4000 0025 0000 3155</div>
                  <div className="card-desc">Visa - Requires 3D Secure</div>
                </div>
                <div className="test-card-item">
                  <div className="card-number">5555 5555 5555 4444</div>
                  <div className="card-desc">Mastercard - Success</div>
                </div>
                <div className="test-card-item">
                  <div className="card-number">4000 0000 0000 9995</div>
                  <div className="card-desc">Visa - Declined (Insufficient Funds)</div>
                </div>
              </div>
              <div className="test-card-details">
                <strong>For all test cards:</strong>
                <ul>
                  <li><strong>Expiry:</strong> Any future date (e.g., 12/34)</li>
                  <li><strong>CVC:</strong> Any 3 digits (e.g., 123)</li>
                  <li><strong>ZIP:</strong> Any 5 digits (e.g., 12345)</li>
                </ul>
              </div>
            </div>
          </>
        )}

        <div className="info-box">
          <strong>Current Stripe Mode:</strong> {settings.sandbox_mode ? 'Test' : 'Live'}<br />
          <strong>Printify Orders:</strong> {settings.sandbox_mode ? 'Disabled' : 'Enabled'}
        </div>
      </section>

      {/* Jokes Ticker Configuration */}
      <section className="settings-section">
        <h2>😄 Jokes Ticker Configuration</h2>
        <p className="section-description">
          Configure the scrolling jokes ticker that appears at the top of the site.
        </p>

        <div className="setting-row">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={settings.jokes_ticker_enabled}
              onChange={(e) => setSettings({ ...settings, jokes_ticker_enabled: e.target.checked })}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-text">
              {settings.jokes_ticker_enabled ? 'Ticker Enabled' : 'Ticker Disabled'}
            </span>
          </label>
        </div>

        <div className="setting-group">
          <label>Joke Categories:</label>
          <div className="category-checkboxes">
            {availableCategories.map(category => (
              <label key={category} className="category-checkbox">
                <input
                  type="checkbox"
                  checked={isCategorySelected(category)}
                  onChange={() => toggleCategory(category)}
                />
                {category}
              </label>
            ))}
          </div>
        </div>

        <div className="setting-group">
          <label>
            Maximum Joke Length: {settings.jokes_max_length} characters
          </label>
          <input
            type="range"
            min="100"
            max="200"
            value={settings.jokes_max_length}
            onChange={(e) => setSettings({ ...settings, jokes_max_length: parseInt(e.target.value) })}
            className="slider"
          />
        </div>

        <div className="setting-group">
          <label>Content Filters (comma-separated):</label>
          <input
            type="text"
            value={settings.jokes_blacklist_flags}
            onChange={(e) => setSettings({ ...settings, jokes_blacklist_flags: e.target.value })}
            placeholder="nsfw,religious,political,racist,sexist,explicit"
            className="text-input"
          />
          <small>These content types will be filtered out from jokes</small>
        </div>

        <button
          onClick={handleTestJokes}
          disabled={testingJokes}
          className="btn-secondary"
        >
          {testingJokes ? 'Fetching...' : 'Test Jokes API'}
        </button>

        {testJokes.length > 0 && (
          <div className="test-jokes">
            <h4>Sample Jokes:</h4>
            <ul>
              {testJokes.map((joke, index) => (
                <li key={index}>{joke}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Seasonal Animations Section */}
      <section className="settings-section">
        <h2>❄️🍂 Seasonal Animations</h2>
        <p className="section-description">
          Enable falling snowflakes for Christmas season or falling leaves for Fall season across the entire site.
        </p>

        <div className="setting-row">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={settings.seasonal_animation_enabled}
              onChange={(e) => setSettings({ ...settings, seasonal_animation_enabled: e.target.checked })}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-text">
              {settings.seasonal_animation_enabled ? 'Animations Enabled' : 'Animations Disabled'}
            </span>
          </label>
        </div>

        {settings.seasonal_animation_enabled && (
          <>
            <div className="setting-group">
              <label>Animation Type:</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="animation_type"
                    value="snow"
                    checked={settings.seasonal_animation_type === 'snow'}
                    onChange={(e) => setSettings({ ...settings, seasonal_animation_type: e.target.value })}
                  />
                  <span>❄️ Snowflakes (Winter/Christmas)</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="animation_type"
                    value="leaves"
                    checked={settings.seasonal_animation_type === 'leaves'}
                    onChange={(e) => setSettings({ ...settings, seasonal_animation_type: e.target.value })}
                  />
                  <span>🍂 Falling Leaves (Fall/Autumn)</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="animation_type"
                    value="valentines"
                    checked={settings.seasonal_animation_type === 'valentines'}
                    onChange={(e) => setSettings({ ...settings, seasonal_animation_type: e.target.value })}
                  />
                  <span>🍆💕 Valentine's Day (Eggplants & Hearts)</span>
                </label>
              </div>
            </div>

            <div className="setting-group">
              <label>
                Number of Items: {settings.seasonal_animation_count}
              </label>
              <input
                type="range"
                min="10"
                max="50"
                value={settings.seasonal_animation_count}
                onChange={(e) => setSettings({ ...settings, seasonal_animation_count: parseInt(e.target.value) })}
                className="slider"
              />
              <small>More items = more visible effect (10-50)</small>
            </div>

            <div className="setting-group">
              <label>Animation Speed:</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="animation_speed"
                    value="slow"
                    checked={settings.seasonal_animation_speed === 'slow'}
                    onChange={(e) => setSettings({ ...settings, seasonal_animation_speed: e.target.value })}
                  />
                  <span>🐢 Slow (Relaxed)</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="animation_speed"
                    value="medium"
                    checked={settings.seasonal_animation_speed === 'medium'}
                    onChange={(e) => setSettings({ ...settings, seasonal_animation_speed: e.target.value })}
                  />
                  <span>⚡ Medium (Balanced)</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="animation_speed"
                    value="fast"
                    checked={settings.seasonal_animation_speed === 'fast'}
                    onChange={(e) => setSettings({ ...settings, seasonal_animation_speed: e.target.value })}
                  />
                  <span>🚀 Fast (Energetic)</span>
                </label>
              </div>
            </div>
          </>
        )}

        <div className="info-box">
          <strong>ℹ️ Note:</strong> Animations will appear on the customer-facing site only, not in the admin panel.
          The animations are optimized for performance and will automatically reduce on mobile devices.
        </div>
      </section>

      {/* Save Button */}
      <div className="settings-actions">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary btn-save"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

export default AdminSettings;
